/**
 * imageCompressor.js
 *
 * Automatic client-side image compression (WhatsApp-style, transparent).
 * Goal: keep all uploaded images ≤ ~1MB without visible quality loss.
 *
 * Behaviour (May 2026 spec):
 *   - If the file is not an image (e.g. PDF, video) → returned untouched.
 *   - If the file is an image ≤ 1MB → returned untouched.
 *   - If the file is an image > 1MB → compressed to ≤ 1MB while keeping
 *     reasonable visual quality (quality cascade 0.85 → 0.7 → 0.55 → 0.4).
 *   - No prompts, toasts, or progress indicators are shown to the user.
 *
 * Public exports:
 *   - compressImage(file) → Promise<File>
 *   - compressImages(files, onProgress?) → Promise<File[]>
 *   - compressFormData(formData) → Promise<FormData>  (axios interceptor helper)
 */

const MAX_DIMENSION = 2400;             // Hard cap on long edge (px)
const TARGET_BYTES = 1024 * 1024;       // 1MB
const SKIP_BYTES = 1024 * 1024;         // Skip files already ≤ 1MB
const QUALITY_CASCADE = [0.85, 0.7, 0.55, 0.4];

function isCompressibleImage(file) {
  if (!file || typeof file !== 'object') return false;
  if (typeof file.type !== 'string') return false;
  if (!file.type.startsWith('image/')) return false;
  // SVG is vector; gif/heic can break the canvas pipeline → leave them alone.
  if (file.type === 'image/svg+xml') return false;
  if (file.type === 'image/gif') return false;
  return true;
}

async function _compressViaOffscreen(bitmap, width, height) {
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, width, height);
  for (const q of QUALITY_CASCADE) {
    const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: q });
    if (blob.size <= TARGET_BYTES) return blob;
  }
  // Last attempt = lowest quality even if still oversized.
  return canvas.convertToBlob({ type: 'image/jpeg', quality: QUALITY_CASCADE[QUALITY_CASCADE.length - 1] });
}

function _compressViaDomCanvas(file) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = async () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      const toBlob = (q) => new Promise((res) => canvas.toBlob((b) => res(b), 'image/jpeg', q));
      let blob = null;
      for (const q of QUALITY_CASCADE) {
        blob = await toBlob(q);
        if (blob && blob.size <= TARGET_BYTES) break;
      }
      if (!blob) { resolve(file); return; }
      const baseName = file.name.replace(/\.[^.]+$/, '');
      resolve(new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() }));
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

/**
 * Compress a single image file. Returns the original file when no
 * compression is needed/possible.
 *
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function compressImage(file) {
  if (!isCompressibleImage(file)) return file;
  if (file.size <= SKIP_BYTES) return file;

  try {
    const bitmap = await createImageBitmap(file);
    let { width, height } = bitmap;
    if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
      const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
    }
    const blob = await _compressViaOffscreen(bitmap, width, height);
    try { bitmap.close(); } catch { /* noop */ }
    const baseName = file.name.replace(/\.[^.]+$/, '');
    const compressed = new File([blob], `${baseName}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
    // If we somehow made it bigger, return the original.
    if (compressed.size >= file.size) return file;
    if (typeof console !== 'undefined') {
      console.log(`[compressImage] ${file.name}: ${(file.size/1024).toFixed(0)}KB → ${(compressed.size/1024).toFixed(0)}KB`);
    }
    return compressed;
  } catch (err) {
    // OffscreenCanvas not supported (older Android/iOS) → DOM canvas fallback
    if (typeof console !== 'undefined') {
      console.warn('[compressImage] OffscreenCanvas failed, fallback path:', err && err.message);
    }
    return _compressViaDomCanvas(file);
  }
}

/**
 * Compress an array of files in sequence (keeps memory low on mobile).
 *
 * @param {File[]} files
 * @param {Function} [onProgress] – called with (done, total)
 * @returns {Promise<File[]>}
 */
export async function compressImages(files, onProgress) {
  const out = [];
  for (let i = 0; i < files.length; i++) {
    out.push(await compressImage(files[i]));
    if (onProgress) onProgress(i + 1, files.length);
  }
  return out;
}

/**
 * Iterate a FormData, compress any image File parts > 1MB, and return a
 * fresh FormData with the compressed parts in the same order. Used by the
 * global axios request interceptor so every multipart upload across the
 * app benefits from compression — no per-call wiring required.
 *
 * @param {FormData} formData
 * @returns {Promise<FormData>}
 */
export async function compressFormData(formData) {
  if (typeof FormData === 'undefined') return formData;
  if (!(formData instanceof FormData)) return formData;
  const entries = Array.from(formData.entries());
  // Fast path: no compressible image > skip threshold.
  const hasBigImage = entries.some(([, v]) =>
    v && typeof v === 'object' && v instanceof File &&
    isCompressibleImage(v) && v.size > SKIP_BYTES
  );
  if (!hasBigImage) return formData;

  const out = new FormData();
  for (const [key, value] of entries) {
    if (value instanceof File && isCompressibleImage(value) && value.size > SKIP_BYTES) {
      try {
        const compressed = await compressImage(value);
        out.append(key, compressed, compressed.name);
      } catch {
        out.append(key, value, value.name);
      }
    } else if (value instanceof File) {
      out.append(key, value, value.name);
    } else {
      out.append(key, value);
    }
  }
  return out;
}
